import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import supabase from '../../lib/supabase';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useOrder } from '../../contexts/OrderContext';
import { useMembership } from '../../contexts/MembershipContext';
import { useAuth } from '../../contexts/AuthContext';

const { FiArrowLeft, FiClock } = FiIcons;

// Convert services_dy3xr row into display model
function convertRow(row) {
  const name = row?.name || 'Service';
  const lastDate = row?.last_date || row?.lastDate || null;
  // Expect detailed_pricing = { online: {cat: {...}}, home: {cat: {...}} }
  const dp = row?.detailed_pricing || {};
  const online = dp.online || {};
  const home = dp.home || {};
  return { name, lastDate, onlinePricing: online, homePricing: home };
}

export default function DynamicServiceDetails() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { createOrder } = useOrder();
  const { canUseForApplication, getMembershipDetails } = useMembership();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [hasMembership, setHasMembership] = useState(false);
  const [membershipDetails, setMembershipDetails] = useState(null);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isAdminPreview = (params.get('preview') === '1') && (user?.role === 'admin');

  const searchPattern = useMemo(() => {
    // Turn slug into loose name pattern
    const tokens = (serviceId || '').replace(/[-_]+/g, ' ').trim();
    return `%${tokens}%`;
  }, [serviceId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1) Prefer a per-service table if we know it for this slug
        const perServiceTables = {
          'jee-mains': 'svc_jee_mains_f5e34b8f',
          'amrita': 'svc_amrita_bfc3b0b6',
          'ap-eapcet': 'svc_ap_eapcet_d211a441',
          'neet': 'svc_neet_00756c41',
          'vit': 'svc_vit_381766f8',
          'visa-applications': 'svc_visa_applications_4a46ed8c',
          'passport-applications': 'svc_passport_applications_5740b2a9',
          'tg-eapcet': 'svc_tg_eapcet_c48ce354',
          'srm': 'svc_srm_srmjeee_70becc04',
          'srm-srmjeee-b-tech': 'svc_srm_srmjeee_70becc04',
          'srm-institute-of-science-and-technology': 'svc_srm_srmjeee_70becc04',
          'bitsat': 'svc_bitsat_a2247cf8',
          'iiit-hyderabad': 'svc_iiit_hyderabad_23ecda96',
          'other-universities': 'svc_other_universities_e6babfe7',
          'job-applications': 'svc_job_applications_1be41f5d',
          'resume-building': 'svc_resume_building_ace6aebe',
          'career-guidance': 'svc_career_guidance_85bbe5fa',
          'government-job-forms': 'svc_government_job_forms_032f1d65',
          'other-government-forms': 'svc_other_government_forms_72d19ea5',
          'document-verification': 'svc_document_verification_4b15b913',
        };

        const tableName = perServiceTables[serviceId];
        if (tableName) {
          try {
            const { data: trows } = await supabase
              .from(tableName)
              .select('data')
              .order('created_at', { ascending: false })
              .limit(1);
            if (trows && trows.length > 0) {
              const payload = trows[0]?.data || {};
              const model = {
                name: payload.name || 'Service',
                lastDate: payload.lastDate || null,
                onlinePricing: payload.onlinePricing || {},
                homePricing: payload.homePricing || {},
                enabled: payload.enabled !== false,
              };
              setData(model);
              setEnabled(model.enabled !== false);
              return; // done
            }
          } catch (terr) {
            console.warn('Per-service fetch failed, falling back:', terr);
            // fall through to generic lookup
          }
        }

        // 2) Fallbacks in order: exact name (case-insensitive), then loose pattern
        const exactName = (serviceId || '').replace(/[-_]+/g, ' ').trim();
        const { data: exactRows, error: exactErr } = await supabase
          .from('services_dy3xr')
          .select('*')
          .ilike('name', exactName)
          .limit(1);
        if (exactErr) throw exactErr;

        let rows = exactRows;
        if (!rows || rows.length === 0) {
          let query = supabase.from('services_dy3xr').select('*');
          query = query.ilike('name', searchPattern).order('name', { ascending: true }).limit(1);
          const { data: looseRows, error } = await query;
          if (error) throw error;
          rows = looseRows;
        }
        const error = null;
        if (error) throw error;
        if (!rows || rows.length === 0) {
          setError('No matching service found in database.');
          setData(null);
          return;
        }
        const row = rows[0];
        const model = convertRow(row);
        const dp = row.detailed_pricing || {};
        const jsonEnabled = (typeof dp.enabled === 'boolean') ? dp.enabled : undefined;
        const rowEnabled = (jsonEnabled !== undefined)
          ? jsonEnabled
          : (row.enabled !== undefined ? row.enabled : (row.is_enabled !== undefined ? row.is_enabled : (row.active !== undefined ? row.active : true)));
        model.enabled = rowEnabled !== false;
        setData(model);
        setEnabled(model.enabled);
      } catch (e) {
        console.error(e);
        setError('Failed to load service details.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchPattern]);

  const handleToggleEnabled = async () => {
    if (!data) return;
    try {
      const exactName = (data.name || '').trim();
      if (!exactName) return;

      // 1) Fetch the exact row id and JSON to update (avoid selecting unknown columns)
      let { data: rows, error: fetchErr } = await supabase
        .from('services_dy3xr')
        .select('id, name, detailed_pricing')
        .ilike('name', exactName)
        .limit(1)
        .maybeSingle();
      if (fetchErr) throw fetchErr;
      if (!rows) {
        // Fallback: loose search using the same pattern as fetch
        const { data: looseRow, error: looseErr } = await supabase
          .from('services_dy3xr')
          .select('id, name, detailed_pricing')
          .ilike('name', searchPattern)
          .order('name', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (looseErr) throw looseErr;
        rows = looseRow;
      }
      if (!rows) throw new Error('Service not found for toggle');

      const row = rows;
      const id = row.id;

      const nextVal = !enabled;
      // 2) Prefer updating JSON flag inside detailed_pricing to avoid schema coupling
      const currentDP = row.detailed_pricing || {};
      const nextDP = { ...currentDP, enabled: nextVal };
      let success = false;
      let lastErr = null;
      try {
        const { error } = await supabase
          .from('services_dy3xr')
          .update({ detailed_pricing: nextDP })
          .eq('id', id);
        if (error) throw error;
        success = true;
      } catch (eJson) {
        lastErr = eJson;
      }

      // 3) If JSON update failed, try each known status column sequentially
      if (!success) {
        const candidates = ['enabled', 'is_enabled', 'active'];
        for (const col of candidates) {
          try {
            const { error } = await supabase
              .from('services_dy3xr')
              .update({ [col]: nextVal })
              .eq('id', id);
            if (!error) { success = true; break; }
            lastErr = error;
          } catch (e) {
            lastErr = e;
          }
        }
      }
      if (!success) throw lastErr || new Error('No supported status field available to update');

      setEnabled(nextVal);
      setData({ ...data, enabled: nextVal });
      toast.success(nextVal ? 'Service enabled' : 'Service disabled');
    } catch (err) {
      console.error('Failed to toggle service status:', err);
      toast.error(`Failed to update service status: ${err?.message || 'Unknown error'}`);
    }
  };

  useEffect(() => {
    const details = getMembershipDetails();
    if (details) {
      setHasMembership(true);
      setMembershipDetails(details);
    } else {
      setHasMembership(false);
    }
  }, [getMembershipDetails]);

  useEffect(() => {
    if (isAdminPreview) return; // do not enforce selection in admin preview
    const selectedType = localStorage.getItem('selectedServiceType');
    if (selectedType) {
      setSelectedServiceType(selectedType);
    } else {
      toast.error('You must choose either Home Services or Online Services to proceed with your application.');
      navigate('/service-type-selection');
      return;
    }
  }, [navigate, isAdminPreview]);

  const renderPricingTable = (title, pricing) => {
    const categories = Object.keys(pricing || {});
    if (categories.length === 0) return (
      <Card className="p-4">
        <div className="text-sm text-gray-600">No pricing available.</div>
      </Card>
    );
    return (
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-3">{title}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600 border-b">
                <th className="py-2 pr-4">Category</th>
                <th className="py-2 pr-4">Exam Fee</th>
                <th className="py-2 pr-4">Service Charge</th>
                <th className="py-2 pr-4">Bank Charges and Taxes</th>
                <th className="py-2 pr-4">Total</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const p = pricing[cat] || {};
                const tableType = title.toLowerCase().includes('home') ? 'home' : 'online';
                const selected = selectedCategory === cat && selectedServiceType === tableType;
                const totalVal = p.total ?? ((p.examFee||0)+(p.serviceCharge||0)+(p.bankCharge||0));
                return (
                  <tr
                    key={cat}
                    className={`border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${selected ? 'bg-blue-50' : ''}`}
                    onClick={() => { setSelectedCategory(cat); setSelectedServiceType(tableType); }}
                  >
                    <td className="py-2 pr-4 font-medium">
                      <div className="flex items-center gap-2">
                        <span className={`${selected ? 'text-gray-900 font-semibold' : ''}`}>{cat}</span>
                        {selected && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            Selected
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4">₹{p.examFee ?? 0}</td>
                    <td className="py-2 pr-4">₹{p.serviceCharge ?? 0}</td>
                    <td className="py-2 pr-4">₹{p.bankCharge ?? 0}</td>
                    <td className="py-2 pr-4 font-semibold">₹{totalVal}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  const calculateTotal = () => {
    if (!data || !selectedCategory || !selectedServiceType) return 0;
    const pricing = selectedServiceType === 'home' ? data.homePricing : data.onlinePricing;
    const priceDetails = pricing[selectedCategory];
    if (!priceDetails) return 0;
    const examFee = priceDetails.examFee || priceDetails.applicationFee || 0;
    const serviceCharge = priceDetails.serviceCharge || priceDetails.serviceFee || 0;
    const bankCharge = priceDetails.bankCharge || 50;
    if (hasMembership && canUseForApplication()) {
      return examFee + bankCharge;
    }
    return examFee + serviceCharge + bankCharge;
  };

  const handleProceed = async () => {
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }
    if (!selectedServiceType) {
      toast.error('Please select a service type');
      return;
    }
    const pricing = selectedServiceType === 'home' ? data.homePricing : data.onlinePricing;
    const priceDetails = pricing[selectedCategory];
    if (!priceDetails) {
      toast.error('Invalid category selection');
      return;
    }
    const useMembership = hasMembership && canUseForApplication();
    const finalServiceCharge = useMembership ? 0 : (priceDetails.serviceCharge || priceDetails.serviceFee);
    const orderData = {
      serviceName: data.name,
      serviceId: serviceId,
      category: selectedCategory,
      serviceType: selectedServiceType,
      examFee: priceDetails.examFee || priceDetails.applicationFee,
      serviceFee: finalServiceCharge,
      bankCharge: priceDetails.bankCharge,
      totalAmount: (priceDetails.examFee || priceDetails.applicationFee) + finalServiceCharge + priceDetails.bankCharge,
      lastDate: data.lastDate,
      useMembership: useMembership,
      membershipId: useMembership ? membershipDetails?.plan?.id : null,
      documents: selectedServiceType === 'online' ? (data.requiredDocuments || [
        'Photo',
        'Signature',
        'SSC Certificate',
        'Aadhaar',
        'Caste Certificate',
      ]) : [],
    };
    const createdOrder = await createOrder(orderData);
    if (!createdOrder) return;
    if (selectedServiceType === 'home') {
      navigate('/payment');
    } else {
      navigate('/upload-documents');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" onClick={() => navigate(-1)} className="flex items-center">
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {loading && (
          <Card className="p-6"><div>Loading service details...</div></Card>
        )}
        {error && !loading && (
          <Card className="p-6"><div className="text-red-600">{error}</div></Card>
        )}
        {!loading && !error && data && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">{data.name}</h1>
              <div className="flex items-center gap-3">
                {!enabled && (
                  <span className="inline-flex items-center px-2 py-1 rounded bg-red-100 text-red-800 text-xs font-medium">
                    Service Disabled
                  </span>
                )}
                {data.lastDate && (
                <div className="flex items-center text-sm text-gray-700">
                  <SafeIcon icon={FiClock} className="w-4 h-4 mr-1" />
                  Last Date: {data.lastDate}
                </div>
                )}
                {user?.role === 'admin' && (
                  <Button variant="outline" size="sm" onClick={handleToggleEnabled}>
                    {enabled ? 'Disable' : 'Enable'} Service
                  </Button>
                )}
              </div>
            </div>
            {!isAdminPreview && !enabled ? (
              <Card className="p-6">
                <div className="text-red-700 font-medium mb-2">This service is currently unavailable.</div>
                <div className="text-sm text-gray-700 mb-4">Please choose another service from the categories.</div>
                <div>
                  <Button variant="outline" onClick={() => navigate('/service-categories')}>Back to Services</Button>
                </div>
              </Card>
            ) : (
              <>
                {isAdminPreview && (
                  <div className="mb-2 inline-flex items-center px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-medium">
                    Admin Preview: checkout disabled
                  </div>
                )}

                {isAdminPreview ? (
                  <>
                    {renderPricingTable('Online Service Pricing', data.onlinePricing)}
                    {renderPricingTable('Home Service Pricing', data.homePricing)}
                  </>
                ) : (
                  <>
                    {selectedServiceType === 'online' && renderPricingTable('Online Service Pricing', data.onlinePricing)}
                    {selectedServiceType === 'home' && renderPricingTable('Home Service Pricing', data.homePricing)}
                  </>
                )}

                {!isAdminPreview && selectedCategory && selectedServiceType && (
                  <Card className="p-6 bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
                    <div className="flex flex-col items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Selected Option</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                          <span>Category: <span className="font-medium">{selectedCategory}</span></span>
                          <span>Service Type: <span className="font-medium capitalize">{selectedServiceType} Service</span></span>
                          <span>Price: <span className="font-semibold text-primary-600">₹{calculateTotal().toString()}</span></span>
                        </div>
                      </div>
                      <Button onClick={handleProceed} className="bg-primary-600 hover:bg-primary-700" disabled={!enabled}>
                        {selectedServiceType === 'home' ? 'Proceed to Payment' : 'Proceed to Upload Documents'}
                      </Button>
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
